import { Plus, Minus, Pencil, CheckCircle2, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export const fmtVal = (v) => {
  if (v === null || v === undefined || v === '') return <span className="italic text-muted-foreground/80">empty</span>;
  if (Array.isArray(v)) return v.length ? v.join(', ') : <span className="italic text-muted-foreground/80">none</span>;
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return String(v);
};

export function Stat({ label, value, tone }) {
  const tones = { add: 'text-emerald-400', remove: 'text-rose-400', change: 'text-amber-400', same: 'text-muted-foreground' };
  return (
    <div className="flex items-center gap-1.5">
      <span className={`text-sm font-heading font-bold ${tones[tone]}`}>{value}</span>
      <span className="text-[10px] text-muted-foreground">{label}</span>
    </div>
  );
}

export function FieldChange({ c }) {
  if (c.field === 'attributes') {
    return (
      <div className="text-[11px] space-y-1 pl-1">
        {c.attributes_added?.length > 0 && <div><span className="text-emerald-400 font-medium">+ fields:</span> {c.attributes_added.join(', ')}</div>}
        {c.attributes_removed?.length > 0 && <div><span className="text-rose-400 font-medium">− fields:</span> {c.attributes_removed.join(', ')}</div>}
        {c.attributes_modified?.map((m, i) => (
          <div key={i}><span className="text-amber-400 font-medium">~ {m.name}:</span> {m.changes.map(ch => `${ch.field}`).join(', ')}</div>
        ))}
      </div>
    );
  }
  return (
    <div className="text-[11px] flex items-center gap-1.5 flex-wrap pl-1">
      <span className="font-medium text-foreground/80">{c.field}:</span>
      <span className="text-rose-400/90 line-through">{fmtVal(c.left)}</span>
      <ArrowRight className="w-3 h-3 text-muted-foreground" />
      <span className="text-emerald-400/90">{fmtVal(c.right)}</span>
    </div>
  );
}

export function DiffSection({ title, icon: Icon, diff, rightLabel = 'only in right', leftLabel = 'only in left' }) {
  if (!diff) return null;
  const total = diff.added.length + diff.removed.length + diff.changed.length;
  return (
    <Card className="glass-card rounded-2xl border-border/50 overflow-hidden" data-testid={`diff-section-${title.toLowerCase()}`}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-border/30 bg-muted/20">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-primary" />
          <span className="font-heading font-semibold text-sm">{title}</span>
        </div>
        <div className="flex items-center gap-4">
          <Stat label="added" value={diff.added.length} tone="add" />
          <Stat label="removed" value={diff.removed.length} tone="remove" />
          <Stat label="changed" value={diff.changed.length} tone="change" />
          <Stat label="unchanged" value={diff.unchanged} tone="same" />
        </div>
      </div>
      {total === 0 ? (
        <div className="px-5 py-4 text-xs text-muted-foreground flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> No differences in {title.toLowerCase()}</div>
      ) : (
        <div className="divide-y divide-border/20">
          {diff.added.map((it, i) => (
            <div key={`a${i}`} className="px-5 py-2.5 flex items-start gap-2" data-testid="diff-added">
              <Plus className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
              <div><span className="text-xs font-medium text-foreground">{it.name}</span> <Badge className="ml-1 text-[8px] bg-emerald-500/15 text-emerald-400 border-emerald-500/20">{rightLabel}</Badge></div>
            </div>
          ))}
          {diff.removed.map((it, i) => (
            <div key={`r${i}`} className="px-5 py-2.5 flex items-start gap-2" data-testid="diff-removed">
              <Minus className="w-3.5 h-3.5 text-rose-400 mt-0.5 flex-shrink-0" />
              <div><span className="text-xs font-medium text-foreground">{it.name}</span> <Badge className="ml-1 text-[8px] bg-rose-500/15 text-rose-400 border-rose-500/20">{leftLabel}</Badge></div>
            </div>
          ))}
          {diff.changed.map((it, i) => (
            <div key={`c${i}`} className="px-5 py-2.5 flex items-start gap-2" data-testid="diff-changed">
              <Pencil className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="text-xs font-medium text-foreground">{it.name}</span>
                <div className="mt-1 space-y-1">{it.changes.map((c, j) => <FieldChange key={j} c={c} />)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function AttrChangeCard({ title, icon: Icon, changes, testid }) {
  if (!changes || changes.length === 0) return null;
  return (
    <Card className="glass-card rounded-2xl border-border/50 overflow-hidden" data-testid={testid}>
      <div className="flex items-center gap-2 px-5 py-3 border-b border-border/30 bg-muted/20"><Icon className="w-4 h-4 text-primary" /><span className="font-heading font-semibold text-sm">{title}</span></div>
      <div className="divide-y divide-border/20">
        {changes.map((c, i) => <div key={i} className="px-5 py-2.5"><FieldChange c={c} /></div>)}
      </div>
    </Card>
  );
}
